import { useEffect, useRef, useState } from "react";
import { hotkeyEventListener } from "@/lib/hotkeyEventListener";
import { toast } from "react-hot-toast";

type Props = {
    legacyUrl: string;
};

// ── Mock Resources Page Component ────────────────────────────────────────────
function MockResourcesPage() {
    const [resources, setResources] = useState([
        { name: 'qb-core', status: 'started', ms: 1.2 },
        { name: 'ox_lib', status: 'started', ms: 0.4 },
        { name: 'ox_inventory', status: 'started', ms: 2.8 },
        { name: 'vrp', status: 'stopped', ms: 0 },
        { name: 'pma-voice', status: 'started', ms: 0.1 },
        { name: 'screenshot-basic', status: 'started', ms: 0.0 },
        { name: 'sc-labs-hud', status: 'started', ms: 0.6 },
        { name: 'old-script', status: 'stopped', ms: 0 },
        { name: 'loading-screen', status: 'started', ms: 0.3 },
    ]);
    const [search, setSearch] = useState('');
    const [runningAction, setRunningAction] = useState<string | null>(null);

    const handleAction = (resName: string, action: string) => {
        const key = `${resName}-${action}`;
        setRunningAction(key);
        const toastId = toast.loading(`Executing resource command: ${action} on ${resName}...`);

        setTimeout(() => {
            setResources(prev => prev.map(r => {
                if (r.name === resName) {
                    return {
                        ...r,
                        status: action === 'stop' ? 'stopped' : 'started',
                        ms: action === 'stop' ? 0 : Math.round((Math.random() * 2 + 0.1) * 10) / 10
                    };
                }
                return r;
            }));
            toast.success(`Resource "${resName}" ${action}ed successfully!`, { id: toastId });
            setRunningAction(null);
        }, 800);
    };

    const filtered = resources.filter(r => r.name.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="flex flex-col gap-6 w-full max-w-5xl mx-auto px-4 py-6">
            <div>
                <h2 className="text-xl font-bold font-display uppercase tracking-wider text-white">Resources Grid</h2>
                <p className="text-xs text-slate-400">Control active Lua and C# scripts inside FiveM server runtime.</p>
            </div>

            <div className="flex justify-between items-center gap-4 flex-wrap">
                <input
                    type="text"
                    placeholder="Search resources..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="bg-white/[0.02] border border-white/10 rounded-xl px-4 py-2.5 text-slate-100 placeholder:text-slate-600 text-xs font-mono focus:outline-none focus:border-[#00ffd1]/50 w-72"
                />
            </div>

            <div className="glass-card overflow-hidden relative">
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00ffd1]/10 to-transparent" />
                <table className="w-full text-left text-xs font-mono">
                    <thead className="bg-white/[0.02] text-slate-400 uppercase tracking-wider border-b border-white/[0.04]">
                        <tr>
                            <th className="px-5 py-3.5">Resource Name</th>
                            <th className="px-5 py-3.5">Status</th>
                            <th className="px-5 py-3.5">CPU Time</th>
                            <th className="px-5 py-3.5 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.04] text-slate-200">
                        {filtered.map(res => (
                            <tr key={res.name} className="hover:bg-white/[0.01] transition-colors">
                                <td className="px-5 py-4 font-semibold text-white">{res.name}</td>
                                <td className="px-5 py-4">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                                        res.status === 'started'
                                            ? 'text-[#00ff66] bg-[#00ff66]/10 border-[#00ff66]/20'
                                            : 'text-slate-400 bg-white/5 border-white/10'
                                    }`}>
                                        {res.status}
                                    </span>
                                </td>
                                <td className="px-5 py-4 text-slate-400">{res.status === 'started' ? `${res.ms} ms` : '-'}</td>
                                <td className="px-5 py-4 text-right flex justify-end gap-2">
                                    <button
                                        disabled={runningAction !== null}
                                        onClick={() => handleAction(res.name, res.status === 'started' ? 'stop' : 'start')}
                                        className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all ${
                                            res.status === 'started'
                                                ? 'bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20'
                                                : 'bg-[#00ffd1]/10 hover:bg-[#00ffd1]/20 text-[#00ffd1] border border-[#00ffd1]/20'
                                        }`}
                                    >
                                        {res.status === 'started' ? 'Stop' : 'Start'}
                                    </button>
                                    <button
                                        disabled={res.status !== 'started' || runningAction !== null}
                                        onClick={() => handleAction(res.name, 'restart')}
                                        className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase bg-[#00ff66]/10 hover:bg-[#00ff66]/20 text-[#00ff66] border border-[#00ff66]/20 disabled:opacity-40"
                                    >
                                        Restart
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── Mock Admins Page Component ────────────────────────────────────────────────
function MockAdminsPage() {
    const [admins, setAdmins] = useState([
        { discord_id: '123456789012345678', username: 'sc0pez', permissions: 'owner', isMaster: true },
        { discord_id: '234567890123456789', username: 'Joker', permissions: 'admin', isMaster: false },
        { discord_id: '345678901234567890', username: 'NeonPulse', permissions: 'mod', isMaster: false },
    ]);
    const [discordId, setDiscordId] = useState('');
    const [username, setUsername] = useState('');
    const [permissions, setPermissions] = useState('files');
    const [showCreate, setShowCreate] = useState(false);
    const [tempPassword, setTempPassword] = useState('');

    const handleCreate = (e: React.FormEvent) => {
        e.preventDefault();
        if (!discordId.trim() || !username.trim()) return;

        const toastId = toast.loading(`Creating user ${username}...`);
        setTimeout(() => {
            const password = 'sc_' + Math.random().toString(36).substring(2, 10).toUpperCase();
            setAdmins(prev => [...prev, {
                discord_id: discordId.trim(),
                username: username.trim(),
                permissions,
                isMaster: false,
            }]);
            setTempPassword(password);
            setDiscordId('');
            setUsername('');
            setShowCreate(false);
            toast.success('Admin created successfully!', { id: toastId });
        }, 600);
    };

    const handleDelete = (id: string) => {
        if (!window.confirm(`Are you sure you want to delete this admin account?`)) return;
        setAdmins(prev => prev.filter(a => a.discord_id !== id));
        toast.success('Admin deleted successfully.');
    };

    return (
        <div className="flex flex-col gap-6 w-full max-w-5xl mx-auto px-4 py-6">
            <div className="flex justify-between items-center flex-wrap gap-4">
                <div>
                    <h2 className="text-xl font-bold font-display uppercase tracking-wider text-white">User Management</h2>
                    <p className="text-xs text-slate-400">Administer panel operators access credentials and resource rights.</p>
                </div>

                <button
                    onClick={() => { setShowCreate(!showCreate); setTempPassword(''); }}
                    className="px-4 py-2 bg-[#00ffd1] hover:bg-[#00ffd1]/90 text-black text-xs font-bold rounded-xl transition-all flex items-center gap-1.5"
                >
                    <i className="fa-solid fa-user-plus" /> Create Operator
                </button>
            </div>

            {tempPassword && (
                <div className="glass-card p-5 border-[#00ff66]/25 bg-[#00ff66]/5 text-slate-100 flex flex-col gap-2 relative">
                    <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00ff66]/20 to-transparent" />
                    <div className="flex items-center gap-2 text-[#00ff66] font-bold text-xs uppercase tracking-wider">
                        <i className="fa-solid fa-key" /> Temporary Credentials Generated
                    </div>
                    <p className="text-xs text-slate-300 mt-1">
                        A direct message has been sent to the user on Discord. In case of blockages, copy the temporary password:
                    </p>
                    <div className="bg-[#030508] p-3 rounded-lg border border-white/5 font-mono text-sm font-bold text-[#00ffd1] select-all inline-block w-fit mt-1">
                        {tempPassword}
                    </div>
                </div>
            )}

            {showCreate && (
                <div className="glass-card p-6 max-w-[450px] relative">
                    <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00ffd1]/20 to-transparent" />
                    <form onSubmit={handleCreate} className="flex flex-col gap-4">
                        <h3 className="text-xs font-bold font-display uppercase tracking-wider text-white">Create Panel User</h3>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">Discord User ID</label>
                            <input
                                type="text"
                                placeholder="Numbers only..."
                                value={discordId}
                                onChange={(e) => setDiscordId(e.target.value)}
                                className="w-full bg-white/[0.02] border border-white/10 rounded-xl px-4 py-2.5 text-slate-100 text-xs font-mono focus:outline-none focus:border-[#00ffd1]/50"
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">Display Name</label>
                            <input
                                type="text"
                                placeholder="Enter operator name..."
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className="w-full bg-white/[0.02] border border-white/10 rounded-xl px-4 py-2.5 text-slate-100 text-xs font-mono focus:outline-none focus:border-[#00ffd1]/50"
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">Permissions</label>
                            <select
                                value={permissions}
                                onChange={(e) => setPermissions(e.target.value)}
                                className="w-full bg-[#080a0f] border border-white/10 rounded-xl px-4 py-2.5 text-slate-100 text-xs font-mono focus:outline-none focus:border-[#00ffd1]/50"
                            >
                                <option value="files">File Explorer Only</option>
                                <option value="files,server">File Explorer + Server Controls</option>
                                <option value="admin">Full Administrator (Audit / Users)</option>
                            </select>
                        </div>
                        <div className="flex justify-end gap-3 mt-2">
                            <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 bg-transparent hover:bg-white/5 border border-transparent rounded-xl text-slate-400 text-xs">Cancel</button>
                            <button type="submit" className="px-4 py-2 bg-[#00ffd1] hover:bg-[#00ffd1]/95 text-black font-bold text-xs rounded-xl">Create User</button>
                        </div>
                    </form>
                </div>
            )}

            <div className="glass-card overflow-hidden relative">
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00ffd1]/10 to-transparent" />
                <table className="w-full text-left text-xs font-mono">
                    <thead className="bg-white/[0.02] text-slate-400 uppercase tracking-wider border-b border-white/[0.04]">
                        <tr>
                            <th className="px-5 py-3.5">Discord ID</th>
                            <th className="px-5 py-3.5">Username</th>
                            <th className="px-5 py-3.5">Permissions</th>
                            <th className="px-5 py-3.5 text-right">Delete Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.04] text-slate-200">
                        {admins.map(user => (
                            <tr key={user.discord_id} className="hover:bg-white/[0.01] transition-colors">
                                <td className="px-5 py-4 text-slate-400 font-bold">{user.discord_id}</td>
                                <td className="px-5 py-4 text-slate-200 font-semibold">{user.username}</td>
                                <td className="px-5 py-4">
                                    <span className={`px-2 py-0.5 rounded text-[10px] ${
                                        user.permissions === 'owner' || user.permissions === 'admin'
                                            ? 'text-[#00ffd1] bg-[#00ffd1]/10 border border-[#00ffd1]/20'
                                            : 'text-slate-400 bg-white/5 border border-white/10'
                                    }`}>
                                        {user.permissions}
                                    </span>
                                </td>
                                <td className="px-5 py-4 text-right">
                                    <button
                                        onClick={() => handleDelete(user.discord_id)}
                                        disabled={user.isMaster}
                                        className="text-slate-500 hover:text-red-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Delete account"
                                    >
                                        <i className="fa-solid fa-trash-can" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── Mock Whitelist Page Component ─────────────────────────────────────────────
function MockWhitelistPage() {
    const [whitelist, setWhitelist] = useState([
        { id: 'WL-9210', type: 'license', value: 'license:abcd123456efgh', description: 'aisc0pez main license', created_at: '2026-05-29T10:00:00Z', created_by: 'sc0pez' },
        { id: 'WL-5812', type: 'discord', value: 'discord:234567890123456789', description: 'Joker Discord whitelist', created_at: '2026-05-30T12:00:00Z', created_by: 'sc0pez' },
        { id: 'WL-1452', type: 'license', value: 'license:cdef123456ghij', description: 'NovaKnight whitelist approval', created_at: '2026-05-30T15:30:00Z', created_by: 'Joker' },
    ]);
    const [type, setType] = useState('license');
    const [value, setValue] = useState('');
    const [description, setDescription] = useState('');
    const [showCreate, setShowCreate] = useState(false);

    const handleCreate = (e: React.FormEvent) => {
        e.preventDefault();
        if (!value.trim()) return;

        const id = 'WL-' + Math.floor(1000 + Math.random() * 9000);
        setWhitelist(prev => [...prev, {
            id,
            type,
            value: value.trim(),
            description: description.trim() || 'No description provided',
            created_at: new Date().toISOString(),
            created_by: 'sc0pez',
        }]);
        setValue('');
        setDescription('');
        setShowCreate(false);
        toast.success('Player whitelisted successfully!');
    };

    const handleDelete = (id: string) => {
        if (!window.confirm('Remove this player from the whitelist?')) return;
        setWhitelist(prev => prev.filter(w => w.id !== id));
        toast.success('Player removed from whitelist.');
    };

    return (
        <div className="flex flex-col gap-6 w-full max-w-5xl mx-auto px-4 py-6">
            <div className="flex justify-between items-center flex-wrap gap-4">
                <div>
                    <h2 className="text-xl font-bold font-display uppercase tracking-wider text-white">Whitelist Manager</h2>
                    <p className="text-xs text-slate-400">Configure license and Discord identifier whitelist entries for server authentication access.</p>
                </div>

                <button
                    onClick={() => setShowCreate(!showCreate)}
                    className="px-4 py-2 bg-[#00ffd1] hover:bg-[#00ffd1]/90 text-black text-xs font-bold rounded-xl transition-all flex items-center gap-1.5"
                >
                    <i className="fa-solid fa-plus" /> Whitelist Player
                </button>
            </div>

            {showCreate && (
                <div className="glass-card p-6 max-w-[450px] relative">
                    <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00ffd1]/20 to-transparent" />
                    <form onSubmit={handleCreate} className="flex flex-col gap-4">
                        <h3 className="text-xs font-bold font-display uppercase tracking-wider text-white">Whitelist Identifier</h3>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">Type</label>
                            <select
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                className="w-full bg-[#080a0f] border border-white/10 rounded-xl px-4 py-2.5 text-slate-100 text-xs font-mono focus:outline-none focus:border-[#00ffd1]/50"
                            >
                                <option value="license">Rockstar License</option>
                                <option value="discord">Discord ID</option>
                            </select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">Identifier Value</label>
                            <input
                                type="text"
                                placeholder={type === 'license' ? 'license:abcd123...' : 'discord:1729133...'}
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                className="w-full bg-white/[0.02] border border-white/10 rounded-xl px-4 py-2.5 text-slate-100 text-xs font-mono focus:outline-none focus:border-[#00ffd1]/50"
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">Notes / Description</label>
                            <input
                                type="text"
                                placeholder="Player nickname, request source..."
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full bg-white/[0.02] border border-white/10 rounded-xl px-4 py-2.5 text-slate-100 text-xs font-mono focus:outline-none focus:border-[#00ffd1]/50"
                            />
                        </div>
                        <div className="flex justify-end gap-3 mt-2">
                            <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 bg-transparent hover:bg-white/5 border border-transparent rounded-xl text-slate-400 text-xs">Cancel</button>
                            <button type="submit" className="px-4 py-2 bg-[#00ffd1] hover:bg-[#00ffd1]/95 text-black font-bold text-xs rounded-xl">Whitelist Entry</button>
                        </div>
                    </form>
                </div>
            )}

            <div className="glass-card overflow-hidden relative">
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00ffd1]/10 to-transparent" />
                <table className="w-full text-left text-xs font-mono">
                    <thead className="bg-white/[0.02] text-slate-400 uppercase tracking-wider border-b border-white/[0.04]">
                        <tr>
                            <th className="px-5 py-3.5">WL ID</th>
                            <th className="px-5 py-3.5">Type</th>
                            <th className="px-5 py-3.5">Value</th>
                            <th className="px-5 py-3.5">Notes</th>
                            <th className="px-5 py-3.5">Created By</th>
                            <th className="px-5 py-3.5 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.04] text-slate-200">
                        {whitelist.map(entry => (
                            <tr key={entry.id} className="hover:bg-white/[0.01] transition-colors">
                                <td className="px-5 py-4 font-bold text-slate-400">{entry.id}</td>
                                <td className="px-5 py-4">
                                    <span className="px-2 py-0.5 rounded text-[9px] uppercase font-extrabold bg-white/5 border border-white/10 text-slate-400">
                                        {entry.type}
                                    </span>
                                </td>
                                <td className="px-5 py-4 text-slate-200 font-semibold">{entry.value}</td>
                                <td className="px-5 py-4 text-slate-400 font-sans">{entry.description}</td>
                                <td className="px-5 py-4 text-slate-500">{entry.created_by}</td>
                                <td className="px-5 py-4 text-right">
                                    <button
                                        onClick={() => handleDelete(entry.id)}
                                        className="text-slate-500 hover:text-red-400 transition-colors p-1"
                                        title="Revoke Whitelist"
                                    >
                                        <i className="fa-solid fa-trash-can" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function Iframe({ legacyUrl }: Props) {
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Listens to hotkeys in the iframe
    useEffect(() => {
        if (!iframeRef.current) return;
        iframeRef.current.contentWindow?.addEventListener('keydown', hotkeyEventListener);
    }, []);

    // ── Local Mock Fallback rendering inside DEV environment ─────────────────
    if (import.meta.env.DEV) {
        if (legacyUrl === 'resources') {
            return <MockResourcesPage />;
        }
        if (legacyUrl === 'adminManager') {
            return <MockAdminsPage />;
        }
        if (legacyUrl === 'whitelist') {
            return <MockWhitelistPage />;
        }

        // Generic mock fallback page
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 font-sans text-center">
                <div className="w-16 h-16 rounded-full border-2 border-dashed border-[#00ffd1] animate-spin" />
                <div>
                    <h3 className="text-lg font-bold text-white uppercase tracking-wider">Page Placeholder</h3>
                    <p className="text-xs text-slate-400 mt-1 font-mono">legacyUrl: <code>{legacyUrl}</code></p>
                </div>
            </div>
        );
    }

    const searchParams = location.search ?? '';
    const hashParams = location.hash ?? '';

    return (
        <iframe
            ref={iframeRef}
            id="legacyPageIframe" //required for the theme switcher
            src={`./legacy/${legacyUrl}${searchParams}${hashParams}`}
            className="w-full"
        ></iframe>
    );
}
